import { useState } from 'react';
import {
  BorderStyle,
  Box,
  Color,
  Display,
  FlexDirection,
  Overflow,
} from '@gousto-internal/citrus-react';
import { PricingDTOTypes } from '@repo/experience-api-client';
import { Card } from '../Card/Card';
import { PeopleSelector } from './PeopleSelector';
import { RecipeSelector } from './RecipeSelector';
import { NudgeBanner } from './NudgeBanner';
import { PriceBlock } from './PriceBlock';
import { BuildYourBoxFooter } from './BuildYourBoxFooter';
import { CARD_RADIUS } from './layout';
import { queueTrackingEvent } from '../../event-tracking/queueTrackingEvent';
import { TRACKING_EVENT_NAMES } from '../../event-tracking/events';

type Props = {
  options: PricingDTOTypes.PricingOptionDTO[];
};

const DEFAULT_PORTIONS_FALLBACK = 4;
const DEFAULT_PORTIONS_FALLBACK_INDEX = 0;

function distinctSorted(values: number[]): number[] {
  return Array.from(new Set(values)).sort((a, b) => a - b);
}

export function PricingExplorer({ options }: Props) {
  const availablePortions = distinctSorted(options.map((option) => option.numberOfPortions));
  const availableRecipes = distinctSorted(options.map((option) => option.numberOfRecipes));
  const defaultPortions = availablePortions.includes(DEFAULT_PORTIONS_FALLBACK)
    ? DEFAULT_PORTIONS_FALLBACK
    : (availablePortions[DEFAULT_PORTIONS_FALLBACK_INDEX] ?? DEFAULT_PORTIONS_FALLBACK);
  const maxRecipes = availableRecipes.length > 0 ? Math.max(...availableRecipes) : 0;

  const [people, setPeople] = useState<number>(defaultPortions);
  const [recipes, setRecipes] = useState<number>(maxRecipes);

  const optionsForSelectedPortions = options.filter((option) => option.numberOfPortions === people);
  const selectedOption = optionsForSelectedPortions.find(
    (option) => option.numberOfRecipes === recipes,
  );
  const bestValueOption = optionsForSelectedPortions.find((option) => option.isBestValue);

  return (
    <Card
      borderColor={Color.ColdGrey_100}
      borderRadius={CARD_RADIUS}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={[5, null, 6]}
    >
      <PeopleSelector value={people} options={availablePortions} onChange={setPeople} />
      <RecipeSelector value={recipes} options={availableRecipes} onChange={setRecipes} />

      {selectedOption ? (
        <Box
          borderColor={Color.ColdGrey_100}
          borderStyle={BorderStyle.Solid}
          borderWidth={0.5}
          borderRadius={CARD_RADIUS}
          overflowX={Overflow.Hidden}
          overflowY={Overflow.Hidden}
        >
          <NudgeBanner
            isBestValue={selectedOption.isBestValue}
            recipesRemainingForBestValue={Math.max(
              0,
              (bestValueOption?.numberOfRecipes ?? recipes) - recipes,
            )}
          />
          <PriceBlock option={selectedOption} />
        </Box>
      ) : null}

      <BuildYourBoxFooter
        onBuildYourBoxClick={() => {
          queueTrackingEvent(TRACKING_EVENT_NAMES.PRICING_SIGNUP_CTA_CLICK, {});
        }}
      />
    </Card>
  );
}
